import { HttpMethod, tryFetch, type HttpResponse } from '@/services/http'
import type { FilterQuery } from '@/shared/models/filter-query.model'
import type { Validation, Shipment } from '@/shared/models/shared.model'
import type { MotionStatusErrorCode } from '../types/motion.enums'
import type { EncryptedKeyValue, ExternalKey, MotionViewStatus } from '../models/motion.model'
import type { MotionBoxErrorCode } from '@/modules/motion-box/types/motion-box.enums'
import type { MotionBoxStatusRequest, NotificationRequest } from './motion.requests'

const BASE_URL = import.meta.env.VITE_MAIN_BASE_URL

const getBoxes = async (
  body: FilterQuery,
): Promise<{ data?: any | null; error?: any; status: number }> =>
  await tryFetch(HttpMethod.Post, BASE_URL, `motion/boxes`, {
    view: true,
    body,
  })

const getStatuses = async (): Promise<
  HttpResponse<MotionViewStatus[] | null, MotionBoxErrorCode>
> =>
  await tryFetch<MotionViewStatus[] | null, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    `motion/status`,
    {
      view: true,
    },
  )

const getStatusValidation = async (body: number[]): Promise<HttpResponse<Validation | null, any>> =>
  await tryFetch<Validation | null, any>(
    HttpMethod.Post,
    BASE_URL,
    'motion/boxes/validate-status',
    {
      view: true,
      body,
    },
  )

const getExternalKey = async (
  motionBoxId: number,
  isVerified: boolean,
): Promise<HttpResponse<ExternalKey, MotionBoxErrorCode>> =>
  await tryFetch<ExternalKey, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    `motion/boxes/${motionBoxId}/external-key?verified=${isVerified}`,
    {
      view: true,
    },
  )

const getDeliveryNote = async (
  motionBoxId: number,
  isVerified: boolean,
): Promise<HttpResponse<EncryptedKeyValue, MotionBoxErrorCode>> =>
  await tryFetch<EncryptedKeyValue, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    `motion/boxes/${motionBoxId}/delivery-note?verified=${isVerified}`,
    {
      view: true,
    },
  )

const getDimensions = async (
  motionBoxId: number,
): Promise<HttpResponse<Record<number, number>, MotionBoxErrorCode>> =>
  await tryFetch<Record<number, number>, MotionBoxErrorCode>(
    HttpMethod.Get,
    BASE_URL,
    `motion/boxes/${motionBoxId}/dimensions`,
    {
      view: true,
    },
  )

const getUpdatedBoxes = async (
  body: any,
): Promise<{ data?: any | null; error?: any; status: number }> =>
  await tryFetch(HttpMethod.Post, BASE_URL, `motion-view/update`, {
    view: true,
    body,
  })

const searchBoxes = async (
  body: any,
): Promise<{ data?: any | null; error?: any; status: number }> =>
  await tryFetch(HttpMethod.Post, BASE_URL, 'search/boxes', { view: true, body })

const getSelectedBox = async (
  motionBoxId: number,
): Promise<{ data?: any | null; error?: any; status: number }> =>
  await tryFetch(HttpMethod.Get, BASE_URL, `motion-view/mbox/${motionBoxId}/select`, { view: true })

const getRelatedMotionBox = async (
  motionBoxId: number,
): Promise<{ data?: any | null; error?: any; status: number }> =>
  await tryFetch(HttpMethod.Get, BASE_URL, `motion/boxes/${motionBoxId}/relations`, { view: true })

const updateStatus = async (
  motionBoxId: number,
  body: MotionBoxStatusRequest,
): Promise<HttpResponse<Shipment | null, MotionStatusErrorCode>> =>
  await tryFetch<Shipment | null, MotionStatusErrorCode>(
    HttpMethod.Patch,
    BASE_URL,
    `motion/boxes/${motionBoxId}/status`,
    {
      view: true,
      body,
    },
  )

const updateDimensions = async (
  motionBoxId: number,
  body: Record<number, number>,
): Promise<HttpResponse<Shipment | null, MotionBoxErrorCode>> =>
  await tryFetch<Shipment | null, MotionBoxErrorCode>(
    HttpMethod.Patch,
    BASE_URL,
    `motion/boxes/${motionBoxId}/dimensions`,
    {
      view: true,
      body,
    },
  )

const createNotification = async (
  motionBoxId: number,
  body: NotificationRequest,
): Promise<HttpResponse<Shipment | null, MotionBoxErrorCode>> =>
  await tryFetch<Shipment | null, MotionBoxErrorCode>(
    HttpMethod.Post,
    BASE_URL,
    `motion/boxes/${motionBoxId}/notification`,
    {
      view: true,
      body,
    },
  )

export const motionViewApi = {
  getBoxes,
  getUpdatedBoxes,
  getStatusValidation,
  getStatuses,
  getExternalKey,
  getDeliveryNote,
  getDimensions,
  updateStatus,
  updateDimensions,
  createNotification,
  searchBoxes,
  getSelectedBox,
  getRelatedMotionBox,
}
